// QA Dashboard — data-quality reporting over the ClickHouse sourcing tables.
// Three tabs:
//   - QA Checks: notebook check results (A1/A2/B/C) and dwd gate verdicts, both from dws_meta_qa_checks
//   - Platform KPIs: 8 KPIs per (workspace, country, platform), current vs previous refresh
//   - Franchise KPIs: live per-franchise KPIs for one (workspace, country)
// The dashone API serves all three under /admin/qa/*, dashmote-admin only.
// The API returns raw per-refresh rows and this page computes Δ% from them, so
// any two refreshes it holds can be compared without the API storing a delta.
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ConfigProvider, Tabs, Table, Collapse, Input, Select, Button, Space, Spin, Alert, Tooltip, Tag, message } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { fetchQaChecks, fetchQaKpis, fetchQaFranchiseKpis } from "../api/qaApi";
import { foldSearch } from "../utils/searchFold";

// App.jsx themes Tabs text white for the dark DashAI nav, so every page on a
// white background restores its own colors or the active label is invisible.
// CassCatalogPage carries the same block for the same reason.
const PAGE_THEME = {
  components: {
    Tabs: {
      itemActiveColor: "#0E49B5",
      itemSelectedColor: "#0E49B5",
      itemHoverColor: "#4080e0",
      itemColor: "rgba(0, 0, 0, 0.45)",
      inkBarColor: "#0E49B5",
    },
  },
};

const PLATFORM_KPIS = [
  "n_outlet", "n_franchise", "n_outlet_with_drink", "n_portfolio",
  "n_portfolio_option", "n_combo", "n_combo_fixed", "n_combo_option",
];
const FRANCHISE_KPIS = [
  "n_outlet", "n_outlet_with_drink", "n_portfolio",
  "n_portfolio_option", "n_combo", "n_combo_fixed", "n_combo_option",
];
const KPI_LABELS = {
  n_outlet: "Outlets",
  n_franchise: "Franchises",
  n_outlet_with_drink: "Outlets w/ drink",
  n_portfolio: "Portfolio",
  n_portfolio_option: "Portfolio options",
  n_combo: "Combos",
  n_combo_fixed: "Combos (fixed)",
  n_combo_option: "Combos (option)",
};
const METRIC_LABELS = {
  total_rows: "Total rows",
  distinct_outlets: "Distinct outlets",
  distinct_rows: "Distinct rows",
  duplicate_rows: "Duplicate rows",
  drink_no_subtype: "Drink, no sub-flag",
  subtype_no_drink: "Sub-flag, no drink",
  orphan_rows: "Orphan rows",
  orphan_outlets: "Orphan outlets",
  violations: "Violations",
  status: "Status",
  blocked: "Blocked",
};

const CHECK_LABELS = {
  a1: "A1 — Outlet duplicates",
  a2: "A2 — Portfolio duplicates",
  b: "B — Flag consistency",
  c: "C — Orphan portfolio rows",
  dup_outlets: "Gate — Outlet duplicates",
  dup_portfolio: "Gate — Portfolio duplicates",
  still_landing: "Gate — Portfolio still landing",
  platform_coverage: "Gate — Platform coverage",
  portfolio_missing: "Gate — Portfolio slice missing",
  dwd_gate: "Gate — Blocked refreshes",
};

// A gate rule's status code. "Not applicable" is a healthy outcome: the slice is
// out of the rule's scope, usually because the gate skipped it over an empty
// portfolio slice. "Timed out" and "Errored" mean the rule reached no verdict,
// which is neither clean nor a violation.
const GATE_STATUS = { PASSED: 0, FAILED: 1, SKIPPED: 2, TIMED_OUT: 3, ERRORED: 4 };
const GATE_STATUS_LABELS = {
  [GATE_STATUS.PASSED]: "Passed",
  [GATE_STATUS.FAILED]: "Failed",
  [GATE_STATUS.SKIPPED]: "Not applicable",
  [GATE_STATUS.TIMED_OUT]: "Timed out",
  [GATE_STATUS.ERRORED]: "Errored",
};

const num = (v) => Number(v ?? 0).toLocaleString("en-US");
const gateStatusName = (v) => GATE_STATUS_LABELS[v] ?? String(v ?? "");

// Two row conventions share this table. The notebook's checks (a1/a2/b/c) emit a
// row only where they found a violation, so a row count is the health summary.
// The dwd gate records every slice it evaluated, pass or fail, so its row count
// says nothing. Each gate rule carries a `status` metric and the gate's own
// verdict carries `blocked`, so the summary reads those instead. Keying off the
// metric rather than a list of check ids lets a new gate rule summarize
// correctly without a change here.
const checkSummary = (items, metricKeys) => {
  if (metricKeys.includes("status")) {
    const failed = items.filter((it) => it.metrics?.status === GATE_STATUS.FAILED).length;
    if (failed) return { color: "red", text: `${num(failed)} failing` };
    const unresolved = items.filter((it) => (it.metrics?.status ?? 0) >= GATE_STATUS.TIMED_OUT).length;
    if (unresolved) return { color: "orange", text: `${num(unresolved)} unresolved` };
    return { color: "green", text: `${num(items.length)} clean` };
  }
  if (metricKeys.includes("blocked")) {
    const blocked = items.filter((it) => it.metrics?.blocked).length;
    if (blocked) return { color: "red", text: `${num(blocked)} blocked` };
    return { color: "green", text: `${num(items.length)} clean` };
  }
  return { color: items.length ? "red" : "green", text: `${num(items.length)} rows` };
};

// prev -> curr percentage change. A dimension with no previous row reads "NEW"
// rather than +100%, and one that was zero and stayed zero reads "–", because a
// percentage against a zero base carries no information.
const pctDelta = (curr, prev) => {
  if (prev > 0) {
    const pct = ((curr - prev) / prev) * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
  }
  return curr > 0 ? "NEW" : "–";
};

const DeltaBadge = ({ curr, prev }) => {
  const d = pctDelta(curr, prev);
  let color = "#8c8c8c";
  if (d.endsWith("%")) color = d.startsWith("-") ? "#cf1322" : parseFloat(d) === 0 ? "#8c8c8c" : "#389e0d";
  else if (d === "NEW") color = "#389e0d";
  return (
    <Tooltip title={`prev ${num(prev)} → curr ${num(curr)}`}>
      <span style={{ marginLeft: 6, fontSize: 11, color }}>{d}</span>
    </Tooltip>
  );
};

// Fold the API's per-refresh rows into one row per dimension-tuple, with
// `${kpi}_curr` / `${kpi}_prev` populated from the matching refresh.
const buildDeltaRows = (rows, dims, kpis, curr, prev) => {
  const map = new Map();
  for (const r of rows || []) {
    const key = dims.map((d) => r[d]).join("||");
    if (!map.has(key)) {
      const base = { key };
      dims.forEach((d) => (base[d] = r[d]));
      kpis.forEach((k) => {
        base[`${k}_curr`] = 0;
        base[`${k}_prev`] = 0;
      });
      map.set(key, base);
    }
    const rec = map.get(key);
    const slot = r.refresh === curr ? "_curr" : r.refresh === prev ? "_prev" : null;
    if (slot) kpis.forEach((k) => (rec[`${k}${slot}`] = Number(r[k] ?? 0)));
  }
  return [...map.values()];
};

const kpiColumns = (kpis) =>
  kpis.map((k) => ({
    title: KPI_LABELS[k] || k,
    dataIndex: `${k}_curr`,
    width: 130,
    sorter: (a, b) => a[`${k}_curr`] - b[`${k}_curr`],
    render: (v, row) => (
      <span>
        {num(v)}
        <DeltaBadge curr={row[`${k}_curr`]} prev={row[`${k}_prev`]} />
      </span>
    ),
  }));

const RefreshHeader = ({ refresh, prevRefresh, onRefresh, loading }) => (
  <Space style={{ marginBottom: 8 }}>
    {refresh ? (
      <span style={{ fontSize: 12, color: "#595959" }}>
        Refresh <Tag>{refresh}</Tag>
        {/* The Checks tab reports one refresh and passes no prevRefresh, so the
            comparison is omitted rather than rendered against an empty tag. */}
        {prevRefresh ? (
          <>
            vs previous <Tag>{prevRefresh}</Tag>
          </>
        ) : null}
      </span>
    ) : null}
    {onRefresh ? (
      <Tooltip title="Bypass the backend 1h cache and reload">
        <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Tooltip>
    ) : null}
  </Space>
);

// Returns a token generator. Each call to next() invalidates every token handed
// out before it, so a response can ask whether it is still the newest.
// destroyInactiveTabPane unmounts a pane on every tab switch, and Load is
// clickable repeatedly, so both a late response and an out-of-order one are
// reachable without this.
const useLatestRequest = () => {
  const seq = useRef(0);
  const live = useRef(true);
  // Set on mount, not only at ref creation. A remount runs the cleanup and then
  // this setup again, so a flag cleared there and never restored would leave
  // every guard false and no request able to clear its own loading state.
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  return useCallback(() => {
    const mine = ++seq.current;
    return () => live.current && mine === seq.current;
  }, []);
};

const LoadingBlock = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
    <Spin size="large" />
  </div>
);

const ApiError = ({ error }) => (
  <Alert
    type="error"
    showIcon
    message="QA data unavailable"
    description={
      <>
        <div>{error}</div>
        <div style={{ marginTop: 8, color: "#595959" }}>
          Ensure the dashone API <code>/admin/qa/*</code> endpoints are deployed
          and the <code>dws_meta_qa_*</code> tables are populated.
        </div>
      </>
    }
  />
);

// --- Tab: Platform KPIs ---
const PlatformKpisTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const claim = useLatestRequest();

  const load = useCallback(({ forceRefresh = false } = {}) => {
    const isCurrent = claim();
    setLoading(true);
    setError(null);
    fetchQaKpis({ forceRefresh })
      .then((resp) => isCurrent() && setData(resp))
      .catch((e) => isCurrent() && setError(e.message))
      .finally(() => isCurrent() && setLoading(false));
  }, [claim]);
  useEffect(() => load(), [load]);

  const rows = useMemo(
    () => buildDeltaRows(data?.rows, ["id_client", "country", "id_platform"], PLATFORM_KPIS, data?.refresh, data?.prev_refresh),
    [data]
  );
  const filtered = useMemo(() => {
    const q = foldSearch(search);
    return q ? rows.filter((r) => foldSearch(`${r.id_client} ${r.country} ${r.id_platform}`).includes(q)) : rows;
  }, [rows, search]);

  // The retry stays on screen through both states. Returning early on error
  // would leave a failed load with no way back except switching tabs.
  if (error) {
    return (
      <div>
        <RefreshHeader onRefresh={() => load({ forceRefresh: true })} loading={loading} />
        <ApiError error={error} />
      </div>
    );
  }
  if (loading) return <LoadingBlock />;

  const columns = [
    { title: "Workspace", dataIndex: "id_client", width: 160, fixed: "left", sorter: (a, b) => a.id_client.localeCompare(b.id_client) },
    { title: "Country", dataIndex: "country", width: 90, sorter: (a, b) => a.country.localeCompare(b.country) },
    { title: "Platform", dataIndex: "id_platform", width: 100 },
    ...kpiColumns(PLATFORM_KPIS),
  ];
  return (
    <div>
      <RefreshHeader refresh={data?.refresh} prevRefresh={data?.prev_refresh} onRefresh={() => load({ forceRefresh: true })} loading={loading} />
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search workspace / country / platform"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 360, marginBottom: 8 }}
      />
      <Table
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1200 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100], showTotal: (t) => `Total ${t}` }}
      />
    </div>
  );
};

// --- Tab: Franchise KPIs (live; requires workspace + country) ---
const FranchiseKpisTab = () => {
  const [idClient, setIdClient] = useState(null);
  const [country, setCountry] = useState(null);
  const [franchiseSearch, setFranchiseSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [scopesError, setScopesError] = useState(null);
  const claim = useLatestRequest();

  // The picker offers only scopes the platform KPIs actually cover, so a
  // selection cannot return an empty table for a workspace/country pair that was
  // never published. That endpoint already returns every (id_client, country) it
  // holds, which is why this needs no workspace-list source of its own.
  const loadScopes = useCallback(() => {
    setScopesError(null);
    return fetchQaKpis()
      .then((resp) => {
        const byClient = new Map();
        for (const row of resp?.rows || []) {
          if (!byClient.has(row.id_client)) byClient.set(row.id_client, new Set());
          byClient.get(row.id_client).add(row.country);
        }
        setScopes(byClient);
      })
      // An empty picker and a failed fetch look identical once rendered, so the
      // failure keeps its own state. Falling back to an empty Map would show the
      // pick-a-scope prompt for what is an outage.
      .catch((e) => setScopesError(e.message));
  }, []);

  useEffect(() => {
    let live = true;
    loadScopes().finally(() => live);
    return () => { live = false; };
  }, [loadScopes]);

  const clientOptions = useMemo(
    () => [...(scopes?.keys() || [])].sort().map((c) => ({ value: c, label: c })),
    [scopes]
  );
  const countryOptions = useMemo(
    () => [...(scopes?.get(idClient) || [])].sort().map((c) => ({ value: c, label: c })),
    [scopes, idClient]
  );

  const load = useCallback(
    ({ forceRefresh = false } = {}) => {
      if (!idClient || !country) {
        message.info("Pick a workspace and country first");
        return;
      }
      const isCurrent = claim();
      setLoading(true);
      setError(null);
      fetchQaFranchiseKpis({ idClient, country, forceRefresh })
        .then((resp) => isCurrent() && setData(resp))
        .catch((e) => isCurrent() && setError(e.message))
        .finally(() => isCurrent() && setLoading(false));
    },
    [idClient, country, claim]
  );

  const rows = useMemo(
    () => buildDeltaRows(data?.rows, ["id_client", "country", "id_platform", "franchise_name"], FRANCHISE_KPIS, data?.refresh, data?.prev_refresh),
    [data]
  );
  const filtered = useMemo(() => {
    const q = foldSearch(franchiseSearch);
    return q ? rows.filter((r) => foldSearch(r.franchise_name).includes(q)) : rows;
  }, [rows, franchiseSearch]);

  const columns = [
    { title: "Franchise", dataIndex: "franchise_name", width: 220, fixed: "left", sorter: (a, b) => (a.franchise_name || "").localeCompare(b.franchise_name || "") },
    { title: "Platform", dataIndex: "id_platform", width: 100 },
    ...kpiColumns(FRANCHISE_KPIS),
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Select
          showSearch
          placeholder="Workspace"
          value={idClient}
          options={clientOptions}
          loading={scopes === null}
          onChange={(v) => { setIdClient(v); setCountry(null); }}
          style={{ width: 220 }}
          filterOption={(input, opt) => foldSearch(opt.value).includes(foldSearch(input))}
        />
        <Select
          showSearch
          placeholder="Country"
          value={country}
          options={countryOptions}
          disabled={!idClient}
          onChange={setCountry}
          style={{ width: 140 }}
          filterOption={(input, opt) => foldSearch(opt.value).includes(foldSearch(input))}
        />
        <Button type="primary" onClick={() => load()} loading={loading} disabled={!idClient || !country}>Load</Button>
        {data ? (
          <Tooltip title="Bypass the backend 1h cache and reload">
            <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={() => load({ forceRefresh: true })} disabled={loading} />
          </Tooltip>
        ) : null}
      </Space>
      {/* Same component as the other two tabs, so the refresh line reads
          identically and the empty-previous case is handled in one place. */}
      <RefreshHeader refresh={data?.refresh} prevRefresh={data?.prev_refresh} />
      {/* One state at a time. A failed load leaves `data` null, so without this
          the error and the pick-a-scope prompt would both render and the prompt
          would ask for the selection the user just made. */}
      {scopesError ? (
        <div>
          <Button size="small" style={{ marginBottom: 8 }} icon={<ReloadOutlined />} onClick={loadScopes}>
            Retry
          </Button>
          <ApiError error={scopesError} />
        </div>
      ) : error ? (
        <ApiError error={error} />
      ) : loading ? (
        <LoadingBlock />
      ) : data ? (
        <>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search franchise name"
            value={franchiseSearch}
            onChange={(e) => setFranchiseSearch(e.target.value)}
            style={{ maxWidth: 320, marginBottom: 8 }}
          />
          <Table size="small" rowKey="key" columns={columns} dataSource={filtered} scroll={{ x: 1100 }}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100], showTotal: (t) => `Total ${t}` }} />
        </>
      ) : (
        <Alert type="info" showIcon message="Pick a workspace and country, then Load." />
      )}
    </div>
  );
};

// --- Tab: QA Checks ---
const QaChecksTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const claim = useLatestRequest();

  const load = useCallback(({ forceRefresh = false } = {}) => {
    const isCurrent = claim();
    setLoading(true);
    setError(null);
    fetchQaChecks({ forceRefresh })
      .then((resp) => isCurrent() && setData(resp))
      .catch((e) => isCurrent() && setError(e.message))
      .finally(() => isCurrent() && setLoading(false));
  }, [claim]);
  useEffect(() => load(), [load]);

  const checkPanels = useMemo(() => {
    const checks = data?.checks || {};
    return Object.keys(checks)
      .sort()
      .map((cid) => {
        const items = checks[cid] || [];
        // Derive metric columns from the union of metric keys present.
        const metricKeys = [...new Set(items.flatMap((it) => Object.keys(it.metrics || {})))].sort();
        const dataSource = items.map((it, i) => ({ key: i, id_client: it.id_client, country: it.country, ...it.metrics }));
        const columns = [
          { title: "Workspace", dataIndex: "id_client", width: 160, sorter: (a, b) => a.id_client.localeCompare(b.id_client) },
          { title: "Country", dataIndex: "country", width: 90 },
          ...metricKeys.map((m) => ({ title: METRIC_LABELS[m] || m, dataIndex: m, width: 150, sorter: (a, b) => (a[m] ?? 0) - (b[m] ?? 0), render: m === "status" ? gateStatusName : num })),
        ];
        const summary = checkSummary(items, metricKeys);
        return {
          key: cid,
          label: (
            <span>
              {CHECK_LABELS[cid] || cid} <Tag color={summary.color}>{summary.text}</Tag>
            </span>
          ),
          children: <Table size="small" rowKey="key" columns={columns} dataSource={dataSource} pagination={{ pageSize: 25, showTotal: (t) => `Total ${t}` }} scroll={{ x: 700 }} />,
        };
      });
  }, [data]);

  if (loading) return <LoadingBlock />;
  if (error) return <ApiError error={error} />;

  return (
    <div>
      <RefreshHeader refresh={data?.refresh} onRefresh={() => load({ forceRefresh: true })} loading={loading} />
      {checkPanels.length ? (
        <Collapse items={checkPanels} defaultActiveKey={checkPanels.map((p) => p.key)} />
      ) : (
        <Alert type="success" showIcon message="No QA check issues found for this refresh." />
      )}
    </div>
  );
};

const QaDashboardPage = () => {
  const items = [
    { key: "checks", label: "QA Checks", children: <QaChecksTab /> },
    { key: "platform", label: "Platform KPIs", children: <PlatformKpisTab /> },
    { key: "franchise", label: "Franchise KPIs", children: <FranchiseKpisTab /> },
  ];
  return (
    <div className="w-[calc(100%-240px)] ml-[240px] min-h-[100vh]" style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>QA Dashboard</h2>
      <ConfigProvider theme={PAGE_THEME}>
        <Tabs defaultActiveKey="checks" items={items} destroyInactiveTabPane />
      </ConfigProvider>
    </div>
  );
};

export default QaDashboardPage;
